import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Col, Flex, Row, Table, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import { countryCodesByCountry } from "../../../Components/Filters/filterOptionsData"
import SearchComponent from "../../../Components/Search"
import Icon from "../../../Icon"
import { getDates, useQuery } from "../../../Utils/utils"
import Globe from "../components/Globe"
import { alertsByCountryColumns, getDashboardProgressRowProps } from "../components/Table/columns"
import Creators from "./../reducer"
import AlertsByCountryMobileTable from "./components/AlertsByCountryMobileTable"

import "../styles.less"

const { Title, Text } = Typography

export const AlertsByCountryPage = ({ usersViewForAdmin, userInfo, dashboard, ...props }) => {
  const pageRef = useRef()
  const navigate = useNavigate()
  const { search } = useLocation()
  let query = useQuery(search)
  let business_account_id = query.get("business_account_id")
  const { t } = useTranslation()
  const helpRef = useRef({ firstLoading: true })
  const isMobile = useSelector((state) => state.nav.isMobile)
  const theme = useSelector((state) => state.nav.theme)
  const businessId =
    usersViewForAdmin && business_account_id
      ? business_account_id
      : userInfo?.current_business_account_id

  const { filters, alertsCountryData } = dashboard
  const extraParams = usersViewForAdmin ? { business_account_id: businessId } : {}

  // The endpoint keys countries by name; the globe keys them by ISO alpha-2. Countries the
  // geometry does not carry (overseas territories the atlas folds into their parent) simply
  // do not plot, and stay visible in the table below.
  const mapData = useMemo(
    () =>
      (alertsCountryData?.tableData ?? []).reduce((acc, row) => {
        const code = countryCodesByCountry[row.label]
        if (code) acc.push({ code, name: row.label, count: row.count, volume: row.volume })
        return acc
      }, []),
    [alertsCountryData]
  )

  // The table rows carry each country's average alert value, plus its share of the period, which
  // also paints the progress underline on the row.
  const tableRows = useMemo(() => {
    const rows = alertsCountryData?.tableData ?? []
    const total = alertsCountryData?.total_count ?? 0
    return rows.map((row) => ({
      ...row,
      average: row.count ? row.volume / row.count : 0,
      percentage: total ? (row.count / total) * 100 : 0
    }))
  }, [alertsCountryData])

  const [countrySearch, setCountrySearch] = useState("")
  const filteredRows = useMemo(() => {
    const term = countrySearch.trim().toLowerCase()
    if (!term) return tableRows
    return tableRows.filter((row) => (row.label ?? "").toLowerCase().includes(term))
  }, [tableRows, countrySearch])

  const cards = useMemo(() => {
    const rows = alertsCountryData?.tableData ?? []
    const count = alertsCountryData?.total_count ?? 0
    const volume = alertsCountryData?.total_volume ?? 0
    const top = rows.reduce((best, row) => (!best || row.count > best.count ? row : best), null)
    return [
      { title: "Total alerts", value: count, icon: "alerts" },
      { title: "Total volume", value: volume, prefix: "$", icon: "money" },
      { title: "Countries", value: rows.length, icon: "globe" },
      { title: "Top country", value: top?.label ?? "-", icon: "pin" }
    ]
  }, [alertsCountryData])

  const handleChangeFilter = useCallback((newFilters) => {
    if (Array.isArray(newFilters.period)) {
      props.changeFilters({
        ...newFilters,
        period: { type: "custom", period: newFilters.period }
      })
    } else {
      props.changeFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    document.title = "Dashboard - Chargeback"
    if (pageRef.current) pageRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      props.getAlertsCountryData({
        chartType: "by_country",
        ...period_query,
        ...extraParams
      })

      if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    }
  }, [filters?.period, businessId])

  const goBack = () => {
    if (usersViewForAdmin) {
      navigate("/admin/dashboard" + (search || ""))
    } else {
      navigate("/dashboard")
    }
  }

  return (
    // Vertical gutter only. A horizontal gutter gives the Row negative side margins, and the
    // page padding lives on the Row itself so nothing absorbs them: the Row then hangs 16px
    // past #main-content, which is overflow-x:auto, and the page scrolls sideways. The columns
    // are all span={24}, so they never needed a horizontal gutter. Dropping it keeps every
    // child at the same x (the -16px margin and the 16px column padding cancelled each other).
    <Row
      ref={pageRef}
      gutter={[0, isMobile ? 12 : 32]}
      className={cls("dashboard-page", {
        "dashboard-page_users_view_for_admin": usersViewForAdmin,
        isMobile: isMobile
      })}
    >
      <Col span={24}>
        <ButtonComponent
          type={"text"}
          onClick={goBack}
          icon={<Icon type={"chevron_left_small"} />}
          style={{ marginLeft: !isMobile ? "-12px" : "-26px" }}
        >
          {t("Back to Overview")}
        </ButtonComponent>
      </Col>

      <Col
        span={24}
        // Careful: React writes `false` as an empty string, so `padding: isMobile && 'unset'`
        // strips the column's gutter padding on desktop and shifts the heading left of the
        // content below it. Only send the overrides when they actually apply.
        style={
          isMobile
            ? { width: "100%", maxWidth: "unset", flex: "unset", padding: "unset" }
            : undefined
        }
      >
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Alerts by Country")}
            </Title>
            <Flex align="center" gap={8}>
              <span className="live-dot" aria-hidden="true" />
              <Text type="secondary">{t("Updates every few minutes")}</Text>
            </Flex>
          </div>

          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </Flex>
      </Col>

      <Col span={24}>
        <div className="globe-hero">
          {!isMobile && <Globe data={mapData} theme={theme} />}

          <Row gutter={[24, 24]} className="globe-cards">
            {cards.map((card) => (
              <Col key={card.title} xs={24} sm={12} lg={6}>
                <DashboardCard data={card} theme={theme} />
              </Col>
            ))}
          </Row>
        </div>
      </Col>

      <Col span={24}>
        <Flex vertical gap={16} className="breakdown-table-header">
          <Title level={5} style={{ marginBottom: 0 }}>
            {t("Country breakdown")}
          </Title>
          <div className="breakdown-search">
            <SearchComponent
              handleSearch={(e) => setCountrySearch(e.target.value)}
              value={countrySearch}
              placeholder={t("Search country")}
            />
          </div>
        </Flex>

        {isMobile ? (
          <AlertsByCountryMobileTable rows={filteredRows} searchTerm={countrySearch} />
        ) : (
          <Table
            className={"chart-table"}
            dataSource={filteredRows}
            columns={alertsByCountryColumns}
            onRow={getDashboardProgressRowProps}
            pagination={false}
            rowKey="label"
            tableLayout="fixed"
            showHeader={!!filteredRows.length}
            locale={{
              emptyText: countrySearch ? (
                <EmptyComponent type="search" style={{ minHeight: 450 }} />
              ) : (
                <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
              )
            }}
          />
        )}
      </Col>
    </Row>
  )
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  dashboard: state.dashboard
})

const mapDispatchToProps = (dispatch) => ({
  getAlertsCountryData: (params) => dispatch(Creators.getAlertsCountryDataRequest(params)),
  changeFilters: (filters) => dispatch(Creators.dashboardSetProp("filters", filters))
})

export default connect(mapStateToProps, mapDispatchToProps)(AlertsByCountryPage)
